/**
 * WebAudio runtime. Renders sounds from the same pure DSP the baker uses, caches
 * the resulting AudioBuffers, and wires the bus/ducking topology from
 * docs/03-AUDIO-BIBLE.md §7.
 *
 * Caching is keyed by (id, variation bucket) rather than by id alone: dice get a
 * small pool of pre-rendered variations so playback stays allocation-free during
 * a roll while still never repeating audibly.
 */

import {
  BUSES,
  DUCK_DB,
  DUCK_RELEASE_S,
  REVERB_RT60_S,
  type BusName,
  type DiceMaterial,
  type Rarity,
} from './materials.js';
import { NoiseRng, dbToGain, type Buffer as SynthBuffer } from './synth.js';
import { SFX_REGISTRY, SAMPLE_RATE, type RenderOptions } from './sfx.js';
import { TRACK_BY_ID, renderTrack } from './music.js';

/** How many pre-rendered variations to keep per sound. */
const VARIATIONS = 6;

export interface PlayOptions extends RenderOptions {
  readonly gain?: number;
  /** Deterministic variation index; omit for a rotating pick. */
  readonly variation?: number;
  readonly delay?: number;
}

type MinimalAudioContext = AudioContext;

export class AudioEngine {
  private ctx: MinimalAudioContext | null = null;
  private master: GainNode | null = null;
  private busNodes = new Map<BusName, GainNode>();
  private reverb: ConvolverNode | null = null;
  private cache = new Map<string, AudioBuffer>();
  private rotation = new Map<string, number>();
  private musicSource: AudioBufferSourceNode | null = null;
  private musicGain: GainNode | null = null;
  private currentTrackId: string | null = null;
  private muted = false;

  get ready(): boolean {
    return this.ctx !== null;
  }

  get trackId(): string | null {
    return this.currentTrackId;
  }

  /**
   * Must be called from a user gesture — browsers refuse to start audio otherwise.
   * Safe to call repeatedly.
   */
  async start(): Promise<void> {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') await this.ctx.resume();
      return;
    }
    const Ctor =
      (globalThis as { AudioContext?: typeof AudioContext }).AudioContext ??
      (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) throw new Error('WebAudio is not available in this environment');

    const ctx = new Ctor({ sampleRate: SAMPLE_RATE });
    this.ctx = ctx;

    this.master = ctx.createGain();
    this.master.gain.value = 0.9;
    this.master.connect(ctx.destination);

    // One shared room, so everything except `tactile` sits in the same space.
    this.reverb = ctx.createConvolver();
    this.reverb.buffer = this.buildImpulseResponse(ctx, REVERB_RT60_S);
    this.reverb.connect(this.master);

    for (const [name, cfg] of Object.entries(BUSES) as [BusName, (typeof BUSES)[BusName]][]) {
      const node = ctx.createGain();
      node.gain.value = cfg.gain;
      node.connect(this.master);
      if (cfg.reverbSend > 0) {
        const send = ctx.createGain();
        send.gain.value = cfg.reverbSend;
        node.connect(send);
        send.connect(this.reverb);
      }
      this.busNodes.set(name, node);
    }
  }

  /** Exponentially decaying noise IR. Cheap, and adequate for a 0.6 s room. */
  private buildImpulseResponse(ctx: MinimalAudioContext, rt60: number): AudioBuffer {
    const length = Math.floor(ctx.sampleRate * rt60);
    const ir = ctx.createBuffer(2, length, ctx.sampleRate);
    const rng = new NoiseRng(0xba5e);
    for (let ch = 0; ch < 2; ch++) {
      const data = ir.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        data[i] = rng.next() * Math.pow(1 - i / length, 2.5);
      }
    }
    return ir;
  }

  private toAudioBuffer(ctx: MinimalAudioContext, buf: SynthBuffer): AudioBuffer {
    const ab = ctx.createBuffer(1, buf.data.length, ctx.sampleRate);
    ab.getChannelData(0).set(buf.data);
    return ab;
  }

  private cacheKey(id: string, variation: number, opts: PlayOptions): string {
    const extra = [opts.material, opts.rarity, opts.face, opts.cursed ? 'c' : '']
      .filter((v) => v !== undefined && v !== '')
      .join(':');
    return `${id}|${variation}|${extra}`;
  }

  private getBuffer(id: string, variation: number, opts: PlayOptions): AudioBuffer | null {
    const ctx = this.ctx;
    if (!ctx) return null;
    const key = this.cacheKey(id, variation, opts);
    const hit = this.cache.get(key);
    if (hit) return hit;

    const spec = SFX_REGISTRY.get(id);
    if (!spec) return null;
    // Seed from the key so a cached variation is identical across sessions.
    const seed = hashKey(key);
    const rendered = spec.render(new NoiseRng(seed), { ...opts, sampleRate: ctx.sampleRate });
    const ab = this.toAudioBuffer(ctx, rendered);
    this.cache.set(key, ab);
    return ab;
  }

  /**
   * Play a sound by audio id. Returns false if the id is unknown, so a missing
   * sound surfaces in development instead of failing silently.
   */
  play(id: string, opts: PlayOptions = {}): boolean {
    const ctx = this.ctx;
    if (!ctx || this.muted) return this.ctx !== null;
    const spec = SFX_REGISTRY.get(id);
    if (!spec) {
      if (typeof console !== 'undefined') console.warn(`[audio] unknown audio id '${id}'`);
      return false;
    }

    const variation = opts.variation ?? this.nextVariation(id);
    const ab = this.getBuffer(id, variation, opts);
    if (!ab) return false;

    const src = ctx.createBufferSource();
    src.buffer = ab;
    const gain = ctx.createGain();
    gain.gain.value = opts.gain ?? 1;
    src.connect(gain);
    gain.connect(this.busNodes.get(spec.bus) ?? this.master!);
    src.start(ctx.currentTime + (opts.delay ?? 0));

    // Dice and cards duck the music so the tactile layer always reads (§7).
    if (spec.bus === 'tactile') this.duckMusic();
    return true;
  }

  private nextVariation(id: string): number {
    const next = ((this.rotation.get(id) ?? 0) + 1) % VARIATIONS;
    this.rotation.set(id, next);
    return next;
  }

  private duckMusic(): void {
    const ctx = this.ctx;
    const music = this.busNodes.get('music');
    if (!ctx || !music) return;
    const base = BUSES.music.gain;
    const now = ctx.currentTime;
    music.gain.cancelScheduledValues(now);
    music.gain.setValueAtTime(music.gain.value, now);
    music.gain.linearRampToValueAtTime(base * dbToGain(DUCK_DB), now + 0.02);
    music.gain.linearRampToValueAtTime(base, now + 0.02 + DUCK_RELEASE_S);
  }

  /** Convenience wrappers so callers do not have to remember id strings. */
  playDice(material: DiceMaterial, face?: number, cursed = false): boolean {
    return this.play(`sfx.dice.roll.${material}`, { material, ...(face !== undefined ? { face } : {}), cursed });
  }

  playReward(rarity: Rarity): boolean {
    return this.play(`sfx.reward.${rarity}`, { rarity });
  }

  /** Start or cross-fade to a music track. A no-op if already playing it. */
  playMusic(trackId: string, fadeSeconds = 1.5): boolean {
    const ctx = this.ctx;
    if (!ctx) return false;
    if (this.currentTrackId === trackId) return true;
    const spec = TRACK_BY_ID.get(trackId);
    if (!spec) {
      if (typeof console !== 'undefined') console.warn(`[audio] unknown track '${trackId}'`);
      return false;
    }

    const key = `music|${trackId}`;
    let ab = this.cache.get(key);
    if (!ab) {
      ab = this.toAudioBuffer(ctx, renderTrack(spec, hashKey(trackId), ctx.sampleRate));
      this.cache.set(key, ab);
    }

    this.stopMusic(fadeSeconds);

    const src = ctx.createBufferSource();
    src.buffer = ab;
    src.loop = true;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.gain.linearRampToValueAtTime(1, ctx.currentTime + fadeSeconds);
    src.connect(gain);
    gain.connect(this.busNodes.get('music') ?? this.master!);
    src.start();

    this.musicSource = src;
    this.musicGain = gain;
    this.currentTrackId = trackId;
    return true;
  }

  stopMusic(fadeSeconds = 1.0): void {
    const ctx = this.ctx;
    const src = this.musicSource;
    const gain = this.musicGain;
    if (!ctx || !src || !gain) return;
    const stopAt = ctx.currentTime + fadeSeconds;
    gain.gain.cancelScheduledValues(ctx.currentTime);
    gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0, stopAt);
    src.stop(stopAt + 0.05);
    this.musicSource = null;
    this.musicGain = null;
    this.currentTrackId = null;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master && this.ctx) {
      this.master.gain.linearRampToValueAtTime(muted ? 0 : 0.9, this.ctx.currentTime + 0.1);
    }
  }

  get isMuted(): boolean {
    return this.muted;
  }

  setBusGain(bus: BusName, gain: number): void {
    const node = this.busNodes.get(bus);
    if (node && this.ctx) {
      node.gain.linearRampToValueAtTime(Math.max(0, gain), this.ctx.currentTime + 0.05);
    }
  }

  /**
   * Pre-render the sounds a session will hit immediately, so the first roll does
   * not stutter while it synthesises.
   */
  warmUp(ids: readonly string[]): void {
    if (!this.ctx) return;
    for (const id of ids) {
      for (let v = 0; v < Math.min(VARIATIONS, 3); v++) this.getBuffer(id, v, {});
    }
  }

  dispose(): void {
    this.stopMusic(0.05);
    this.cache.clear();
    this.rotation.clear();
    void this.ctx?.close();
    this.ctx = null;
    this.master = null;
    this.busNodes.clear();
  }
}

function hashKey(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Sounds worth pre-rendering before the first turn. */
export const WARMUP_IDS: readonly string[] = [
  'sfx.dice.roll.wood',
  'sfx.dice.roll.metal',
  'sfx.card.draw',
  'sfx.card.flip',
  'sfx.move.step',
  'sfx.ui.click',
  'sfx.combat.hit',
  'sfx.reward.common',
];
